import React from 'react'
import clsx from 'clsx'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import {
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Icon,
  ListItem,
  Tooltip,
  Typography,
  Popover,
  List,
  ListItemText,
  Collapse,
} from '@material-ui/core'
import { grey } from '@material-ui/core/colors'
import { useHistory, useLocation } from 'react-router'
import logo2 from 'assets/img/logo2.png'
import { AuthContext, ManagementContext } from 'context'
import { UserMenu } from 'components'
import { Alert } from '@material-ui/lab'

const drawerWidth = 260
const mainNavWidth = 60

// ***** Limita dónde aplicar la agrupación *****
const GROUPED_MODULE_IDS = new Set<string>(['documents']) // <-- cambia 'documents' por tu moduleId real
// Si prefieres por nombre, activa la comparación en shouldGroupHere más abajo.

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { display: 'flex' },
    menuButton: {},
    hide: { display: 'none' },
    drawer: { width: drawerWidth, flexShrink: 0 },
    drawerPaper: { width: drawerWidth },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
      justifyContent: 'flex-end',
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      marginLeft: -drawerWidth,
      backgroundColor: grey[50],
      height: '100vh',
      width: '100%',
    },
    contentShift: {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    },
    mainNav: { width: mainNavWidth, backgroundColor: grey[50], height: '100%' },
    mainNavIcon: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(1) / 2,
    },
    subNav: { flexGrow: 1, display: 'flex', flexDirection: 'column' },
    alertIcon: {
      width: mainNavWidth,
      paddingRight: theme.spacing(2),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: theme.spacing(1),
    },
    alertIconCompact: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 0,
    },
    alertRoot: { padding: 0 },
    alertMessage: {
      fontSize: '0.75em',
      height: 'auto',
      display: 'flex',
      alignItems: 'center',
      letterSpacing: 0.5,
    },
    nested: { paddingLeft: theme.spacing(4) },
    sectionHeader: { fontWeight: 900, letterSpacing: '0.5px', fontSize: '0.75em' },
  })
)

// --- Util: ¿el texto está TODO en mayúsculas (ignorando espacios, signos y dígitos)? ---
const isAllCaps = (s: string) => {
  const onlyLetters = s.normalize('NFD').replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g, '')
  if (onlyLetters.length === 0) return false
  return onlyLetters === onlyLetters.toUpperCase()
}

// --- Agrupa views por títulos en mayúsculas ---
type ViewItem = { name: string; relativePath: string }
type Group = { title: ViewItem; items: ViewItem[] }

const groupByUppercaseTitles = (views: ViewItem[]): Group[] => {
  const groups: Group[] = []
  let current: Group | null = null
  for (const v of views) {
    if (isAllCaps((v.name || '').trim())) {
      // Nuevo encabezado
      current = { title: v, items: [] }
      groups.push(current)
    } else {
      if (!current) {
        // Primer item no es mayúsculas: crear grupo con él como título
        current = { title: v, items: [] }
        groups.push(current)
      } else {
        current.items.push(v)
      }
    }
  }
  return groups
}

const Nav: React.FC = ({ children }) => {
  const { user, navigation, searchingUser, selectedNav, setSelectedNav, userApproval } =
    React.useContext(AuthContext)
  const { approvalLevels } = React.useContext(ManagementContext)
  const classes = useStyles()
  const [open, setOpen] = React.useState(true)
  const handleDrawerOpen = () => setOpen(true)
  const handleDrawerClose = () => setOpen(false)
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)
  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({})
  const history = useHistory()
  const location = useLocation()
  const validAuth = React.useCallback(
    () => Boolean(user) && !Boolean(searchingUser),
    [user, searchingUser]
  )

  // ¿Aplicar agrupación en este módulo?
  const shouldGroupHere = React.useMemo(() => {
    if (!selectedNav) return false
    const byId = selectedNav.moduleId && GROUPED_MODULE_IDS.has(String(selectedNav.moduleId))
    const byName =
      selectedNav.name &&
      (selectedNav.name.toLowerCase() === 'retenciones recibidas' ||
        selectedNav.name.toLowerCase() === 'facturas recibidas')
    return Boolean(byId || byName)
  }, [selectedNav])

  const views = (selectedNav?.views ?? []) as ViewItem[]
  const groups: Group[] = React.useMemo(
    () => (shouldGroupHere ? groupByUppercaseTitles(views) : []),
    [shouldGroupHere, views]
  )

  // Inicializa expansión por grupo al cambiar de módulo
  React.useEffect(() => {
    const init: Record<number, boolean> = {}
    groups.forEach((_, idx) => (init[idx] = false))
    setExpanded(init)
  }, [selectedNav?.moduleId, groups.length])

  const toggleGroup = (idx: number) => setExpanded((p) => ({ ...p, [idx]: !p[idx] }))

  // ---------- Render del subnav (Drawer / Popover) ----------
  const renderSubnavContent = (compact: boolean) => {
    if (!selectedNav) return null

    // Modo agrupado sólo donde corresponde
    if (shouldGroupHere && groups.length > 0) {
      return (
        <List disablePadding dense={compact}>
          {groups.map((g, idx) => (
            <React.Fragment key={g.title.relativePath || `g-${idx}`}>
              {/* Encabezado */}
              <ListItem
                button
                selected={location.pathname === g.title.relativePath}
                onClick={() => history.push(g.title.relativePath)}
              >
                <ListItemText primary={g.title.name} />
                <Icon
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleGroup(idx)
                  }}
                >
                  {expanded[idx] ? 'expand_less' : 'expand_more'}
                </Icon>
              </ListItem>

              {/* Hijos */}
              {g.items.length > 0 && (
                <Collapse in={expanded[idx]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense={compact}>
                    {g.items.map((it) => (
                      <ListItem
                        key={it.relativePath}
                        button
                        className={classes.nested}
                        selected={location.pathname === it.relativePath}
                        onClick={() => history.push(it.relativePath)}
                      >
                        <ListItemText primary={it.name} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      )
    }

    // Modo plano (todos los otros módulos)
    return (
      <List disablePadding dense={compact}>
        {views.map((nav) => (
          <ListItem
            key={nav.relativePath}
            button
            selected={location.pathname === nav.relativePath}
            onClick={() => history.push(nav.relativePath)}
          >
            <ListItemText primary={nav.name} />
          </ListItem>
        ))}
      </List>
    )
  }

  return (
    <div className={classes.root}>
      {validAuth() && (
        <React.Fragment>
          <CssBaseline />
          {/* Popover (modo compacto) */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
            transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          >
            {selectedNav && (
              <div className="p-2" onMouseLeave={() => setAnchorEl(null)}>
                <Typography
                  variant="button"
                  className={`flex-grow pl-4 ${classes.sectionHeader}`}
                  color="primary"
                >
                  {selectedNav.name.toUpperCase()}
                </Typography>
                <div className="mt-2">{renderSubnavContent(true)}</div>
              </div>
            )}
          </Popover>

          {/* Barra lateral compacta */}
          {!open && (
            <div
              style={{ width: mainNavWidth, backgroundColor: grey[50] }}
              className="h-screen flex flex-col items-center py-2 ml-5"
            >
              <IconButton
                color="inherit"
                onClick={handleDrawerOpen}
                className={clsx(classes.menuButton, open && classes.hide)}
              >
                <Icon>menu</Icon>
              </IconButton>
              <div className="py-1 px-2 flex-grow">
                {navigation.map((nav) => (
                  <ListItem
                    key={nav.moduleId}
                    selected={selectedNav?.moduleId === nav.moduleId}
                    className={classes.mainNavIcon}
                    button
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget)
                      setSelectedNav(nav)
                    }}
                  >
                    <Icon
                      style={{
                        color: selectedNav?.moduleId === nav.moduleId ? 'white' : grey[500],
                      }}
                    >
                      {nav.icon}
                    </Icon>
                  </ListItem>
                ))}
              </div>
              {userApproval && (
                <div className="cursor-pointer">
                  <Tooltip
                    arrow
                    title={`Nivel de aprobación ${approvalLevels.find((a) => a.approvalLevelId === userApproval.approvalLevelId)?.index ?? ''}`}
                  >
                    <Alert
                      color="warning"
                      severity="success"
                      classes={{ icon: classes.alertIconCompact }}
                    />
                  </Tooltip>
                </div>
              )}
              <UserMenu onLogout={() => setSelectedNav(undefined)} compact />
            </div>
          )}

          {/* Drawer principal */}
          <Drawer
            className={classes.drawer}
            variant="persistent"
            anchor="left"
            open={open}
            classes={{ paper: classes.drawerPaper }}
          >
            <div className="w-full h-full flex overflow-x-hidden">
              {/* Columna de íconos */}
              <div className={classes.mainNav}>
                <div className={classes.drawerHeader}>
                  <img src={logo2} alt="Logo" />
                </div>
                <div className="py-1 px-2">
                  {navigation.map((nav) => (
                    <Tooltip arrow key={nav.moduleId} title={nav.name} placement="right">
                      <ListItem
                        selected={selectedNav?.moduleId === nav.moduleId}
                        onClick={() => setSelectedNav(nav)}
                        className={classes.mainNavIcon}
                        button
                      >
                        <Icon
                          style={{
                            color: selectedNav?.moduleId === nav.moduleId ? 'white' : grey[500],
                          }}
                        >
                          {nav.icon}
                        </Icon>
                      </ListItem>
                    </Tooltip>
                  ))}
                </div>
              </div>

              {/* Columna de subnavegación */}
              {selectedNav && (
                <div className={classes.subNav}>
                  <div className={classes.drawerHeader} style={{ backgroundColor: '#ffff' }}>
                    <Typography
                      variant="button"
                      className={`${classes.sectionHeader} flex-grow pl-4`}
                      style={{ color: 'black' }}
                    >
                      {selectedNav.name.toUpperCase()}
                    </Typography>
                    <IconButton onClick={handleDrawerClose} style={{ color: 'black' }}>
                      <Icon fontSize="small">chevron_left</Icon>
                    </IconButton>{' '}
                  </div>
                  <Divider />
                  <div className="py-2 px-3 flex-grow overflow-y-auto">
                    {renderSubnavContent(false)}
                  </div>
                </div>
              )}
            </div>

            {userApproval && (
              <div className="px-2 cursor-pointer mt-2">
                <Tooltip arrow title="Nivel de aprobación para solicitudes de ordenes de compra">
                  <Alert
                    color="warning"
                    severity="success"
                    classes={{
                      icon: classes.alertIcon,
                      root: classes.alertRoot,
                      message: classes.alertMessage,
                    }}
                  >
                    {`Nivel de aprobación ${approvalLevels.find((a) => a.approvalLevelId === userApproval.approvalLevelId)?.index ?? ''}`}
                  </Alert>
                </Tooltip>
              </div>
            )}
            <UserMenu onLogout={() => setSelectedNav(undefined)} />
          </Drawer>
        </React.Fragment>
      )}

      <main
        className={`${validAuth() ? clsx(classes.content, { [classes.contentShift]: open }) : 'w-screen h-screen overflow-y-hidden'} overflow-x-hidden`}
      >
        {children}
      </main>
    </div>
  )
}

export default Nav
